import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { supabase } from '../lib/supabase';
import {
  fetchTrackedPackages,
  fetchTrackingAnalytics,
  fetchAllPackagesForExport,
  fetchSingleTrackingStatus,
  fetchLastSyncInfo,
  getPostNLTrackingUrl,
  type TrackedPackage,
  type TrackingAnalytics,
  type PackagesResponse,
  type LastSyncInfo,
} from '../lib/trackingService';
import { exportTrackingToCSV } from '../utils/trackingExport';
import { formatTrackingStatusLabel, getStatusBadgeClasses } from '../utils/trackingStatusHelpers';
import { getAdminId } from '../utils/getAdminId';
import { PackageDetailPanel } from '../components/tracking/PackageDetailPanel';
import { LiveTrackingBanner } from '../components/tracking/LiveTrackingBanner';
import { Package, Search, Download, CircleCheck as CheckCircle, Truck, Clock, TriangleAlert as AlertTriangle, Copy, ExternalLink, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, WifiOff, Eye, Loader as Loader2, MapPin, Signal, Radio, CircleHelp as HelpCircle, ShieldQuestion, Calendar, RefreshCw, History } from 'lucide-react';
import type { RealtimeChannel } from '@supabase/supabase-js';

const DELIVERY_STATUSES = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending_pickup', label: 'Pre-announced' },
  { value: 'in_transit_all', label: 'In Transit (all)' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'out_for_delivery', label: 'Out for Delivery' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'unconfirmed', label: 'Unconfirmed' },
  { value: 'no_info', label: 'No Info from PostNL' },
  { value: 'issues', label: 'Issues (all)' },
  { value: 'failed_delivery', label: 'Failed Delivery' },
  { value: 'returned', label: 'Returned' },
];

const SORT_OPTIONS = [
  { value: 'order_date:desc', label: 'Newest First' },
  { value: 'order_date:asc', label: 'Oldest First' },
  { value: 'customer_name:asc', label: 'Customer A-Z' },
  { value: 'delivery_status:asc', label: 'Status' },
];

const DATE_RANGE_OPTIONS = [
  { value: 30, label: 'Last 30 days' },
  { value: 60, label: 'Last 60 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 180, label: 'Last 6 months' },
  { value: 365, label: 'Last year' },
  { value: 0, label: 'All time' },
];

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subValue?: string;
  gradient: string;
  onClick?: () => void;
}> = ({ icon, label, value, subValue, gradient, onClick }) => (
  <div
    className={`relative overflow-hidden rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-5 transition-all duration-200 ${
      onClick ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02]' : ''
    }`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
        {subValue && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subValue}</p>
        )}
      </div>
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
    </div>
    <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${gradient}`} />
  </div>
);

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return '-';
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 0) return 'Just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const STALE_THRESHOLD_DAYS = 3;

function isStaleStatus(pkg: TrackedPackage): boolean {
  if (!['pending_pickup', 'no_info'].includes(pkg.delivery_status)) return false;
  if (!pkg.last_tracking_update) return false;
  const diffMs = Date.now() - new Date(pkg.last_tracking_update).getTime();
  return diffMs > STALE_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
}

export const PackageTracing: React.FC = () => {
  const { user } = useAuth();
  const { success: notifySuccess, error: notifyError } = useNotification();

  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState<TrackedPackage[]>([]);
  const [analytics, setAnalytics] = useState<TrackingAnalytics | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<TrackedPackage | null>(null);
  const [lastSyncInfo, setLastSyncInfo] = useState<LastSyncInfo | null>(null);
  const [recheckingIds, setRecheckingIds] = useState<Set<string>>(new Set());

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOption, setSortOption] = useState('order_date:desc');
  const [daysBack, setDaysBack] = useState(() => {
    const saved = localStorage.getItem('packageTracing_daysBack');
    return saved ? parseInt(saved, 10) : 60;
  });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalPackages, setTotalPackages] = useState(0);
  const PAGE_SIZE = 50;

  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);

  const realtimeRef = useRef<RealtimeChannel | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const adminId = useMemo(() => {
    if (!user) return null;
    return getAdminId(user);
  }, [user]);

  const loadPackages = useCallback(async (showLoader = true) => {
    if (!adminId) return;
    if (showLoader) setLoading(true);

    try {
      const [sortBy, sortDir] = sortOption.split(':');
      const result: PackagesResponse = await fetchTrackedPackages(adminId, {
        search: debouncedSearch,
        statusFilter,
        sortBy,
        sortDir,
        page,
        pageSize: PAGE_SIZE,
        daysBack,
      });

      setPackages(result.packages);
      setTotalPages(result.total_pages);
      setTotalPackages(result.total);
      setLastUpdated(new Date());
    } catch (err) {
      notifyError('Failed to load packages');
    } finally {
      setLoading(false);
    }
  }, [adminId, debouncedSearch, statusFilter, sortOption, page, daysBack, notifyError]);

  const loadAnalytics = useCallback(async () => {
    if (!adminId) return;
    try {
      const data = await fetchTrackingAnalytics(adminId, daysBack);
      setAnalytics(data);
    } catch {
      // Silent fail for analytics
    }
  }, [adminId, daysBack]);

  const loadLastSyncInfo = useCallback(async () => {
    try {
      const info = adminId ? await fetchLastSyncInfo(adminId) : null;
      setLastSyncInfo(info);
    } catch {
      // Silent fail
    }
  }, [adminId]);

  const handleRecheck = useCallback(async (pkg: TrackedPackage, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!adminId || recheckingIds.has(pkg.id)) return;
    setRecheckingIds(prev => new Set(prev).add(pkg.id));
    try {
      const result = await fetchSingleTrackingStatus(pkg.tracking_code, adminId);
      setPackages(prev =>
        prev.map(p =>
          p.id === pkg.id
            ? {
                ...p,
                delivery_status: result.deliveryStatus,
                estimated_delivery: result.estimatedDelivery,
                last_tracking_update: new Date().toISOString(),
                is_estimated: false,
              }
            : p
        )
      );
      loadAnalytics();
      notifySuccess(`${pkg.order_number}: ${formatTrackingStatusLabel(result.deliveryStatus)}`);
    } catch {
      notifyError('Failed to re-check tracking status');
    } finally {
      setRecheckingIds(prev => {
        const next = new Set(prev);
        next.delete(pkg.id);
        return next;
      });
    }
  }, [adminId, recheckingIds, loadAnalytics, notifySuccess, notifyError]);

  useEffect(() => {
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchTerm]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  useEffect(() => {
    loadLastSyncInfo();
  }, [loadLastSyncInfo]);

  useEffect(() => {
    if (!adminId) return;

    const channel = supabase
      .channel(`package-tracking-${adminId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'shopify_orders',
          filter: `admin_id=eq.${adminId}`,
        },
        (payload) => {
          const row = payload.new as any;
          if (!row?.tracking_code) return;

          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            loadPackages(false);
            loadAnalytics();
          }
        }
      )
      .subscribe((status) => {
        setIsRealtimeConnected(status === 'SUBSCRIBED');
      });

    realtimeRef.current = channel;
    return () => {
      if (realtimeRef.current) {
        supabase.removeChannel(realtimeRef.current);
      }
    };
  }, [adminId, loadPackages, loadAnalytics]);

  const handleLiveSyncComplete = useCallback(() => {
    loadPackages(false);
    loadAnalytics();
    loadLastSyncInfo();
  }, [loadPackages, loadAnalytics, loadLastSyncInfo]);

  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (!adminId) return;
    setExporting(true);
    try {
      const allPackages = await fetchAllPackagesForExport(adminId, {
        search: debouncedSearch,
        statusFilter,
        daysBack,
      });
      if (allPackages.length === 0) {
        notifyError('No packages to export');
        return;
      }
      exportTrackingToCSV(allPackages);
      notifySuccess(`Exported ${allPackages.length} packages to CSV`);
    } catch {
      notifyError('Failed to export packages');
    } finally {
      setExporting(false);
    }
  }, [adminId, debouncedSearch, statusFilter, daysBack, notifySuccess, notifyError]);

  const handleCopyCode = useCallback((code: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(code);
    notifySuccess('Tracking code copied');
  }, [notifySuccess]);

  const handleStatusFilterClick = useCallback((status: string) => {
    setStatusFilter(status);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  }, [totalPages]);

  const progressBarSegments = useMemo(() => {
    if (!analytics || analytics.total_tracked === 0) return [];
    return [
      { pct: analytics.delivered_pct, color: 'bg-emerald-500', label: 'Delivered' },
      { pct: analytics.in_transit_pct + analytics.out_for_delivery_pct, color: 'bg-blue-500', label: 'In Transit' },
      { pct: analytics.pending_pct, color: 'bg-amber-500', label: 'Pending' },
      { pct: analytics.unconfirmed_pct, color: 'bg-gray-400', label: 'Unconfirmed' },
      { pct: analytics.no_info_pct, color: 'bg-yellow-500', label: 'No Info' },
      { pct: analytics.failed_pct + analytics.returned_pct, color: 'bg-red-500', label: 'Issues' },
    ].filter((s) => s.pct > 0);
  }, [analytics]);

  if (loading && packages.length === 0) {
    return (
      <div className="p-4 lg:p-6 xl:p-8 w-full">
        <div className="mb-8">
          <div className="h-10 w-56 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2" />
          <div className="h-5 w-80 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-8">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-2xl animate-pulse" />
          ))}
        </div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse mb-8" />
        <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse mb-6" />
        <div className="space-y-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
            <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 xl:p-8 w-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
              Package Tracing
            </h1>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                PostNL shipment tracking -- {DATE_RANGE_OPTIONS.find(o => o.value === daysBack)?.label || 'Last 60 days'}
                {lastUpdated && (
                  <span className="ml-2">
                    -- Updated {lastUpdated.toLocaleTimeString()}
                  </span>
                )}
              </p>
              <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                isRealtimeConnected
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
              }`}>
                {isRealtimeConnected ? (
                  <>
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                    </span>
                    Live
                  </>
                ) : (
                  <>
                    <WifiOff className="h-3 w-3" />
                    Offline
                  </>
                )}
              </div>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                PostNL
              </span>
              {analytics && analytics.confirmed_count > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400">
                  <Signal className="h-3 w-3" />
                  {analytics.confirmed_count} confirmed
                </span>
              )}
              {analytics && analytics.estimated_count > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                  <Radio className="h-3 w-3" />
                  {analytics.estimated_count} estimated
                </span>
              )}
              {lastSyncInfo && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400" title={`Auto-sync: ${lastSyncInfo.packages_checked} checked, ${lastSyncInfo.packages_updated} updated`}>
                  <History className="h-3 w-3" />
                  Auto-sync {relativeTime(lastSyncInfo.run_at)}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={exporting || totalPackages === 0}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              <span className="hidden sm:inline">{exporting ? 'Exporting...' : 'Export CSV'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Live Tracking Sync Banner */}
      {adminId && analytics && (
        <LiveTrackingBanner
          adminId={adminId}
          totalTracked={analytics.total_tracked}
          estimatedCount={analytics.estimated_count}
          onComplete={handleLiveSyncComplete}
        />
      )}

      {/* Stat Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 lg:gap-4 mb-6">
          <StatCard
            icon={<Package className="h-5 w-5 text-white" />}
            label="Total Tracked"
            value={analytics.total_tracked.toLocaleString()}
            subValue="100% PostNL"
            gradient="from-teal-500 to-cyan-500"
          />
          <StatCard
            icon={<CheckCircle className="h-5 w-5 text-white" />}
            label="Delivered"
            value={analytics.delivered_count.toLocaleString()}
            subValue={`${analytics.delivered_pct}% delivery rate`}
            gradient="from-emerald-500 to-green-500"
            onClick={() => handleStatusFilterClick('delivered')}
          />
          <StatCard
            icon={<Truck className="h-5 w-5 text-white" />}
            label="In Transit"
            value={(analytics.in_transit_count + analytics.out_for_delivery_count).toLocaleString()}
            subValue={`${(analytics.in_transit_pct + analytics.out_for_delivery_pct).toFixed(1)}% of total`}
            gradient="from-blue-500 to-cyan-500"
            onClick={() => handleStatusFilterClick('in_transit_all')}
          />
          <StatCard
            icon={<Clock className="h-5 w-5 text-white" />}
            label="Pending"
            value={analytics.pending_count.toLocaleString()}
            subValue={`${analytics.pending_pct}% awaiting pickup`}
            gradient="from-amber-500 to-orange-500"
            onClick={() => handleStatusFilterClick('pending_pickup')}
          />
          <StatCard
            icon={<HelpCircle className="h-5 w-5 text-white" />}
            label="Unconfirmed"
            value={analytics.unconfirmed_count.toLocaleString()}
            subValue={`${analytics.unconfirmed_pct}% no confirmation`}
            gradient="from-gray-400 to-gray-500"
            onClick={() => handleStatusFilterClick('unconfirmed')}
          />
          <StatCard
            icon={<ShieldQuestion className="h-5 w-5 text-white" />}
            label="No Info"
            value={analytics.no_info_count.toLocaleString()}
            subValue={`${analytics.no_info_pct}% no PostNL data`}
            gradient="from-yellow-500 to-amber-500"
            onClick={() => handleStatusFilterClick('no_info')}
          />
          <StatCard
            icon={<AlertTriangle className="h-5 w-5 text-white" />}
            label="Issues"
            value={(analytics.failed_count + analytics.returned_count).toLocaleString()}
            subValue={`${(analytics.failed_pct + analytics.returned_pct).toFixed(1)}% need attention`}
            gradient="from-red-500 to-rose-500"
            onClick={() => handleStatusFilterClick('issues')}
          />
        </div>
      )}

      {/* Delivery Progress Bar */}
      {analytics && analytics.total_tracked > 0 && (
        <div className="mb-6">
          <div className="h-3 rounded-full overflow-hidden flex bg-gray-200 dark:bg-gray-700">
            {progressBarSegments.map((seg, i) => (
              <div
                key={i}
                className={`${seg.color} transition-all duration-500`}
                style={{ width: `${seg.pct}%` }}
                title={`${seg.label}: ${seg.pct}%`}
              />
            ))}
          </div>
          <div className="flex items-center gap-4 mt-2 flex-wrap">
            {progressBarSegments.map((seg, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                <div className={`w-2.5 h-2.5 rounded-full ${seg.color}`} />
                {seg.label} ({seg.pct}%)
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search / Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by order number, customer name, or tracking code..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all placeholder:text-gray-400"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
        >
          {DELIVERY_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <select
            value={daysBack}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              setDaysBack(val);
              localStorage.setItem('packageTracing_daysBack', String(val));
              setPage(1);
            }}
            className="pl-9 pr-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent appearance-none"
          >
            {DATE_RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <select
          value={sortOption}
          onChange={(e) => { setSortOption(e.target.value); setPage(1); }}
          className="px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
        >
          {SORT_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {totalPackages > 0
            ? `Showing ${((page - 1) * PAGE_SIZE) + 1}-${Math.min(page * PAGE_SIZE, totalPackages)} of ${totalPackages.toLocaleString()} packages`
            : 'No packages found'}
        </p>
        {statusFilter !== 'all' && (
          <button
            onClick={() => { setStatusFilter('all'); setPage(1); }}
            className="text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 font-medium"
          >
            Clear filter
          </button>
        )}
      </div>

      {/* Table */}
      {packages.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-12 text-center">
          <Package className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {totalPackages === 0 && statusFilter === 'all' && !debouncedSearch
              ? 'No Tracked Packages'
              : 'No Results Found'}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            {totalPackages === 0 && statusFilter === 'all' && !debouncedSearch
              ? 'Packages with tracking codes from your Shopify orders will appear here automatically.'
              : 'Try adjusting your search or filters to find what you\'re looking for.'}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden lg:block bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Order</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Customer</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tracking Code</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Service</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Destination</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Shipped</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Update</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                  {packages.map((pkg) => {
                    const city = pkg.shipping_address?.city || '';
                    const country = pkg.shipping_address?.country_code || pkg.shipping_address?.country || '';
                    const destination = [city, country].filter(Boolean).join(', ');
                    const trackUrl = getPostNLTrackingUrl(pkg.tracking_code, pkg.shipping_address);

                    return (
                      <tr
                        key={pkg.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer"
                        onClick={() => setSelectedPackage(pkg)}
                      >
                        <td className="px-4 py-3">
                          <span className="font-semibold text-sm text-teal-700 dark:text-teal-400">
                            {pkg.order_number}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-900 dark:text-gray-100">
                            {pkg.customer_name || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
                              {pkg.tracking_code}
                            </span>
                            <button
                              onClick={(e) => handleCopyCode(pkg.tracking_code, e)}
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-0.5"
                              title="Copy code"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                            <a
                              href={trackUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 p-0.5"
                              title="Track on PostNL.nl"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {pkg.service_type === 'mailbox' ? 'Buspakje' : pkg.service_type === 'package' ? 'Pakje' : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs text-gray-600 dark:text-gray-400">
                            {pkg.product_code || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {destination ? (
                            <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              {destination}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(pkg.delivery_status)}`}>
                              {formatTrackingStatusLabel(pkg.delivery_status)}
                            </span>
                            {pkg.is_estimated && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400" title="Status estimated from shipping date.">
                                Est.
                              </span>
                            )}
                            {isStaleStatus(pkg) && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800" title="Status unchanged for 3+ days — click Re-check to update">
                                <AlertTriangle className="h-2.5 w-2.5" />
                                Stale
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {pkg.fulfilled_at
                            ? new Date(pkg.fulfilled_at).toLocaleDateString()
                            : new Date(pkg.order_date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className="text-sm text-gray-500 dark:text-gray-400"
                            title={pkg.last_tracking_update ? new Date(pkg.last_tracking_update).toLocaleString() : 'No tracking data yet'}
                          >
                            {pkg.last_tracking_update ? relativeTime(pkg.last_tracking_update) : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={(e) => handleRecheck(pkg, e)}
                              disabled={recheckingIds.has(pkg.id)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                              title="Re-check live status from PostNL"
                            >
                              <RefreshCw className={`h-3 w-3 ${recheckingIds.has(pkg.id) ? 'animate-spin' : ''}`} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPackage(pkg);
                              }}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
                            >
                              <Eye className="h-3.5 w-3.5" />
                              Track
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-3">
            {packages.map((pkg) => {
              const trackUrl = getPostNLTrackingUrl(pkg.tracking_code, pkg.shipping_address);
              return (
                <div
                  key={pkg.id}
                  onClick={() => setSelectedPackage(pkg)}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:border-teal-300 dark:hover:border-teal-700 transition-colors cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="font-semibold text-sm text-teal-700 dark:text-teal-400">
                        {pkg.order_number}
                      </span>
                      <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                        {pkg.customer_name || '-'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(pkg.delivery_status)}`}>
                        {formatTrackingStatusLabel(pkg.delivery_status)}
                      </span>
                      {pkg.is_estimated && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                          Est.
                        </span>
                      )}
                      {isStaleStatus(pkg) && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800" title="Status unchanged for 3+ days">
                          <AlertTriangle className="h-2.5 w-2.5" />
                          Stale
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono text-xs text-gray-600 dark:text-gray-400 truncate flex-1">
                      {pkg.tracking_code}
                    </span>
                    <button
                      onClick={(e) => handleCopyCode(pkg.tracking_code, e)}
                      className="text-gray-400 hover:text-gray-600 p-0.5 flex-shrink-0"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                    <a
                      href={trackUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-gray-400 hover:text-teal-600 p-0.5 flex-shrink-0"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  {(pkg.service_type || pkg.product_code) && (
                    <div className="flex items-center gap-2 mb-1.5 text-xs text-gray-500 dark:text-gray-400">
                      <span>{pkg.service_type === 'mailbox' ? 'Buspakje' : pkg.service_type === 'package' ? 'Pakje' : '-'}</span>
                      <span className="text-gray-300 dark:text-gray-600">·</span>
                      <span className="font-mono">{pkg.product_code || '-'}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>
                      {pkg.fulfilled_at
                        ? new Date(pkg.fulfilled_at).toLocaleDateString()
                        : new Date(pkg.order_date).toLocaleDateString()}
                    </span>
                    <div className="flex items-center gap-2">
                      <span>{pkg.last_tracking_update ? relativeTime(pkg.last_tracking_update) : '-'}</span>
                      <button
                        onClick={(e) => handleRecheck(pkg, e)}
                        disabled={recheckingIds.has(pkg.id)}
                        className="p-1 rounded-lg text-gray-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors disabled:opacity-50"
                        title="Re-check live status from PostNL"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${recheckingIds.has(pkg.id) ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Showing {((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, totalPackages)} of {totalPackages.toLocaleString()} packages
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={page === 1}
                  className="px-2 py-2 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="px-2 py-2 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {(() => {
                  const pages: (number | string)[] = [];
                  if (totalPages <= 7) {
                    for (let i = 1; i <= totalPages; i++) pages.push(i);
                  } else {
                    pages.push(1);
                    if (page > 3) pages.push('...');
                    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
                      if (!pages.includes(i)) pages.push(i);
                    }
                    if (page < totalPages - 2) pages.push('...');
                    if (!pages.includes(totalPages)) pages.push(totalPages);
                  }
                  return pages.map((p, idx) =>
                    typeof p === 'number' ? (
                      <button
                        key={idx}
                        onClick={() => handlePageChange(p)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                          p === page
                            ? 'bg-teal-600 text-white shadow-lg shadow-teal-500/25'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {p}
                      </button>
                    ) : (
                      <span key={idx} className="px-2 text-gray-400">...</span>
                    )
                  );
                })()}

                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  className="px-2 py-2 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={page === totalPages}
                  className="px-2 py-2 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Go to:</span>
                <input
                  key={page}
                  type="number"
                  min={1}
                  max={totalPages}
                  defaultValue={page}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    const val = parseInt((e.target as HTMLInputElement).value);
                    if (!Number.isNaN(val) && val >= 1 && val <= totalPages) {
                      handlePageChange(val);
                    }
                  }}
                  className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Panel */}
      {selectedPackage && (
        <PackageDetailPanel
          pkg={selectedPackage}
          adminId={adminId || ''}
          onClose={() => setSelectedPackage(null)}
          onStatusUpdated={(updatedPkg) => {
            setPackages((prev) =>
              prev.map((p) => (p.id === updatedPkg.id ? { ...p, ...updatedPkg } : p))
            );
            loadAnalytics();
          }}
        />
      )}
    </div>
  );
};
